'use strict';

namespace Core.Hotels {

    /*
     Describes services for a single hotel e.g. hotel information and reviews
     */
    angular
        .module('core')
        .service('hotelService', hotelService);

    export interface HotelService {
        //TODO - temporarily we will return HotelWithRooms but really this should be split into two
        //separate functions I think. Not even sure that it is right to extend StaticHotel with rooms
        //rather than just have a separate HotelAvailability interface?
        getHotel(searchId : string|null|undefined,
                 estabId : number,
                 tripId? :string): ng.IPromise<HotelWithRooms>;
        getHotelDetails(req : DetailsRequest): ng.IPromise<Details>;
        rehydrate(detailsId: string): ng.IPromise<HotelDetails>;
        getUrl(): string;
        trackHotelClick(estabId: number,
                                 hotelIndex: number,
                                 boostReason: BoostReason,
                                 sortCriterion: number,
                                 searchId?: string): void;
        getPaginationMode(destinationId: string): boolean;
        getSimilarHotels(estabId : number): Promise<any>;
    }

    function hotelService(hotelFromSession: HotelFromSessionService,
                          $filter,
                          $http : IHttpService,
                          $q : ng.IQService,
                          imageService,
                          broker: Broker,
                          $cacheFactory : ng.ICacheFactoryService,
                          tripService: TripService,
                          $routeParams,
                          fieldGroupService : FieldGroupService,
                          cmsService) {

        const domain = TRPCONFIG.Domain;
        const url: string = '/api/hotels/';
        const hotelInfoCache = $cacheFactory('hotelInfoCache');
        const promiseCache = $cacheFactory.get('detailPromiseCache') || $cacheFactory('detailPromiseCache');

        const service : HotelService = {
            getUrl,
            getHotelDetails,
            //This is used to rehydrate the basket's hotel product
            rehydrate,
            getHotel,
            trackHotelClick,
            getPaginationMode,
            getSimilarHotels
        };

        const fieldFlags = fieldGroupService.getFieldGroup(
            FieldType.Global,
            FieldType.Titles,
            FieldType.IndicativePrice,
            FieldType.ReviewSnippets,
            FieldType.Description,
            FieldType.Url,
            FieldType.Summary,
            FieldType.Characteristics,
            FieldType.Distances,
            FieldType.Facilities,
            FieldType.RoomDetails,
            FieldType.LegacyFacilities,
            FieldType.Recommendations,
            FieldType.EstabGroupIds,
            FieldType.Airports,
            FieldType.AltTitles,
            FieldType.HotelAwards
        );

        const fieldTypes = fieldGroupService.getFieldGroup(
            FieldType.Global,
            FieldType.Titles,
            FieldType.Url,
            FieldType.EstabGroupIds,
        );

        function getUrl(): string {
            return url;
        }

        function getHotelDetails(req : DetailsRequest): ng.IPromise<Details> {
            return $http.post<Details>(url +'details/getdetails', req, {
                customErrCallback() {
                    broker.publish('hotelDetailCallFailed', req);
                    return true;
                }
            }).then(function (res) {
                return res.data;
            });
        }

        function rehydrate(detailsId: string): ng.IPromise<HotelDetails> {
            if (!promiseCache.get<ng.IPromise<HotelDetails>>(detailsId)) {
                promiseCache.put(detailsId,
                    $http.post<Details>(url + 'details/getdetailsbyid', {DetailsId: detailsId})
                    .then(function (res) {
                        return service.getHotel(null, res.data.Itinerary.EstabId, $routeParams.tripId)
                            .then((h: HotelWithRooms) => {
                                return <HotelDetails>angular.extend({}, h, res.data);
                            });
                    }));
            }
            return promiseCache.get<ng.IPromise<HotelDetails>>(detailsId);
        }

        function getHotel(searchId : string|null|undefined, estabId : number, tripId? :string)
            : ng.IPromise<HotelWithRooms> {
            const promises : ng.IPromise<any>[] = [];

            if (searchId && tripId) {
                promises.push(hotelFromSession.getHotel(searchId, estabId, tripId));
            } else {
                promises.push(
                    hotelInfoCache.get(String(estabId)) ||
                        $http.get<StaticHotel>(url+ 'static/gethotel', {
                            params: {
                                fieldsX: 'MinCost,OriginalCost',
                                Id: estabId,
                                CultureCode: domain.CultureCode,
                                DomainId: domain.DomainId,
                                FieldFlags: fieldFlags
                            },
                            cache: true
                        })
                );
            }

            promises.push(tripId ? tripService.getCurrency(tripId) : $q.when());

            if (TRPCONFIG.Domain.OverrideHotelInformationFromCms) {
                promises.push(cmsService.getHotelInformation(estabId));
            }

            return $q.all(promises)
                .then(function (results) {
                    const hotel = _.assign<any, any, HotelWithRooms>({}, results[0].data || results[0]);
                    const currency = <string>results[1] || TRPCONFIG.Domain.CurrencyCode;
                    const cmsHotelInformation = results[2];
                    hotelInfoCache.put(String(estabId), hotel);
                    hotel.ImageUrl = imageService.getMainImageUrl(estabId,
                        TRPCONFIG.DeviceSettings.HotelDetailImageSize,
                        TRPCONFIG.DeviceSettings.HotelDetailImageSize,
                        true);
                    hotel.ObjectTypeId = 1;
                    hotel.TeaserStr = $filter('roundedCurrency')(hotel.IndicativeMinPrice, currency);
                    hotel.HasReviewSnippets = !_.isEmpty(hotel.ReviewSnippets);

                    if (cmsHotelInformation) {
                        hotel.HotelName = cmsHotelInformation.HotelName;
                        hotel.Address = cmsHotelInformation.Address;
                        let imageList: any[]|null = _.get(cmsHotelInformation, 'ImageUrls', []);
                        if(imageList && imageList.length > 0){
                            imageList = imageList.filter((e) => {
                                if (e != null) {
                                    return e;
                                }
                            });
                        }
                        hotel.CmsImageUrls = imageList && imageList.length > 0 ? imageList : null;
                        hotel.Description = cmsHotelInformation.ReplaceDefaultDescription ?
                            cmsHotelInformation.Description :
                        hotel.Description + cmsHotelInformation.Description;
                        hotel.IsPlatinumHotel = cmsHotelInformation.IsPlatinumHotel;
                        hotel.HideFacilitiesList = cmsHotelInformation.HideFacilitiesList;
                        hotel.HideRoomFacilitiesList = cmsHotelInformation.HideRoomFacilitiesList;
                        hotel.IsBoutiqueHotel = cmsHotelInformation.IsBoutiqueHotel;
                        hotel.IsDsfHotel = cmsHotelInformation.IsDsfHotel;
                    }

                    //various other bits of code assume that the rooms array will always exist
                    if(hotel.Rooms == null) {
                        hotel.Rooms = [];
                    }

                    return hotel;
                });
        }

        function trackHotelClick(
            estabId: number,
            hotelIndex: number,
            boostReason: BoostReason,
            sortCriterion: number,
            searchId: string): void
        {
            $http.post('/api/useractions/selections', {
                Type: 2,
                EstabId: estabId,
                DomainId: _.get(TRPCONFIG, 'Domain.DomainId'),
                SearchResultsIndex: hotelIndex,
                SortCriterion: sortCriterion,
                BoostReason: boostReason,
                SearchId: searchId
            }, {ignoreErrors: true});
        }

        async function getSimilarHotels(estabId: number): Promise<any> {
            const config = {
                method: 'POST',
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  EstabId: estabId,
                  CultureCode: TRPCONFIG.Domain.CultureCode,
                  CurrencyCode: TRPCONFIG.Domain.CurrencyCode,
                  DomainId: TRPCONFIG.Domain.DomainId,
                  Radius: 10,
                  ResultsToTakeFromNearestHotels: 3,
                  ResultsToTakeFromSeoRanking: 3,
                  FieldFlags: fieldTypes,
                  FilterCriteria: {
                      Stars: [3, 4, 5],
                      MinNumberOfRatings: 1,
                      OnlyShowAvailable: true
                   },
                   PageStrand: 1
                })
              };
            const response = await fetch('/api/hotels/static/gethotelalternatives', config);

            if (!response.ok) {
                throw Error(response.statusText);
            }

            const data = await response.json();

            return data || [];
        }


        function getPaginationMode(destinationId: string): boolean {
            if (domain.PagingDisabled) {
                return domain.PagingDisabledForDestinations.find( id => id === destinationId );
            }
            return false;
        }

        return service;
    }
}
